import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import TransactionList from '../components/TransactionList';
import TransactionFilters from '../components/TransactionFilters';
import AddTransactionDialog from '../components/AddTransactionDialog';
import { formatDate, getTransactionTypeLabel, getErrorMessage } from '../lib/utils';
import { formatCurrency } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { X, FileText, Trash2, Pencil } from 'lucide-react';
import { PermissionBanner } from '../components/PermissionBanner';

const Transactions = () => {
  const { currency, canEdit, isViewer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    category_id: '',
    account_id: '',
    start_date: '',
    end_date: ''
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  useEffect(() => {
    loadData();
    
    const handleRefresh = () => loadTransactions();
    window.addEventListener('transaction-created', handleRefresh);
    return () => window.removeEventListener('transaction-created', handleRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      const [catRes, accRes] = await Promise.all([
        api.get('/categories'),
        api.get('/accounts')
      ]);
      setCategories(catRes.data);
      setAccounts(accRes.data.filter(a => ['cash', 'bank', 'mobile_money'].includes(a.account_subtype)));
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.type) params.append('transaction_type', filters.type);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/transactions?${params.toString()}`);
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTransaction = async (txn) => {
    try {
      const response = await api.get(`/transactions/${txn.id}`);
      setSelectedTransaction(response.data);
    } catch (error) {
      toast.error('Failed to load transaction details');
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await api.post(`/transactions/${selectedTransaction.id}/void`, { reason: voidReason });
      toast.success('Transaction voided');
      setVoidDialogOpen(false);
      setSelectedTransaction(null);
      setVoidReason('');
      loadTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to void transaction'));
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: '',
      category_id: '',
      account_id: '',
      start_date: '',
      end_date: ''
    });
  };

  const openEditTransaction = (txn) => {
    setEditingTransaction(txn);
    setSelectedTransaction(null);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = (open) => {
    setEditDialogOpen(open);
    if (!open) {
      setEditingTransaction(null);
      loadTransactions();
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
          Transactions
        </h1>
        <p className="text-[hsl(var(--muted-ink))] mt-1">
          View and manage all your financial transactions
        </p>
      </div>

      {/* Permission Banner for Viewers */}
      <PermissionBanner 
        show={isViewer} 
        message="You have read-only access to transactions. Contact an admin to request edit permissions."
      />

      <TransactionFilters
        filters={filters}
        setFilters={setFilters}
        categories={categories}
        accounts={accounts}
        onClear={clearFilters}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-lg">
            {transactions.length} Transaction{transactions.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionList
            transactions={transactions}
            onSelect={handleSelectTransaction}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              Transaction Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge className={`
                  ${selectedTransaction.transaction_type === 'revenue' ? 'bg-green-100 text-green-700' : ''}
                  ${selectedTransaction.transaction_type === 'expense' ? 'bg-red-100 text-red-700' : ''}
                  ${selectedTransaction.transaction_type === 'transfer' ? 'bg-blue-100 text-blue-700' : ''}
                `}>
                  {getTransactionTypeLabel(selectedTransaction.transaction_type)}
                </Badge>
                <span className="text-sm text-[hsl(var(--muted-ink))]">
                  {selectedTransaction.reference_number}
                </span>
              </div>

              <div className={`text-3xl font-bold numbered ${
                selectedTransaction.transaction_type === 'revenue' ? 'text-green-600' :
                selectedTransaction.transaction_type === 'expense' ? 'text-red-600' :
                'text-gray-900'
              }`}>
                {selectedTransaction.transaction_type === 'expense' ? '-' : '+'}
                {formatCurrency(selectedTransaction.amount, selectedTransaction.currency || currency)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[hsl(var(--muted-ink))]">Date</p>
                  <p className="font-medium">{formatDate(selectedTransaction.date)}</p>
                </div>
                <div>
                  <p className="text-[hsl(var(--muted-ink))]">Status</p>
                  <Badge className={selectedTransaction.status === 'voided' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}>
                    {selectedTransaction.status}
                  </Badge>
                </div>
                {selectedTransaction.category && (
                  <div>
                    <p className="text-[hsl(var(--muted-ink))]">Category</p>
                    <p className="font-medium">{selectedTransaction.category.name}</p>
                  </div>
                )}
                {selectedTransaction.payment_account && (
                  <div>
                    <p className="text-[hsl(var(--muted-ink))]">Account</p>
                    <p className="font-medium">{selectedTransaction.payment_account.name}</p>
                  </div>
                )}
              </div>

              {selectedTransaction.description && (
                <div>
                  <p className="text-sm text-[hsl(var(--muted-ink))]">Description</p>
                  <p>{selectedTransaction.description}</p>
                </div>
              )}

              {selectedTransaction.notes && (
                <div>
                  <p className="text-sm text-[hsl(var(--muted-ink))]">Notes</p>
                  <p className="text-sm">{selectedTransaction.notes}</p>
                </div>
              )}

              {selectedTransaction.status !== 'voided' && canEdit && (
                <div className="pt-4 border-t flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditTransaction(selectedTransaction)}
                    data-testid="edit-transaction-button"
                  >
                    <Pencil size={16} className="mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setVoidDialogOpen(true)}
                    data-testid="void-transaction-button"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Void Transaction
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Void Transaction</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Please provide a reason for voiding this transaction.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <textarea
              className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-[hsl(var(--brand-500))] focus:border-transparent"
              rows={3}
              placeholder="Reason for voiding..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              data-testid="void-reason-input"
            />
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setVoidDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleVoid}>
                Void Transaction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <AddTransactionDialog 
        open={editDialogOpen} 
        onOpenChange={handleEditDialogClose}
        editTransaction={editingTransaction}
      />
    </div>
  );
};

export default Transactions;
